import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import Iconify from 'src/components/Iconify';
import mktAccountsService from 'src/services/mktAccountsService';
import { SOCIAL_PROFILE_DESIGN as d, METRIC_TONES } from './SocialProfile.design';
import {
    IG_NODE_METRICS, IG_PERFORMANCE_METRICS, IG_SERIES,
    FB_NODE_METRICS, FB_PERFORMANCE_METRICS, FB_SERIES,
    MEDIA_PRODUCT_ORDER, PERIODOS,
    formatMetricValue, formatHora, metricasDisponiveis,
} from './socialMetrics.catalog';

// Motivos que o usuario NAO consegue resolver concedendo permissao — mostrar um
// CTA de "reconectar" neles mandaria a pessoa para um caminho que nao conserta.
const SEM_ACAO_DO_USUARIO = ['MIN_FOLLOWERS', 'MIN_PAGE_LIKES', 'INVALID_METRIC', 'RATE_LIMITED'];

const CATALOGO = {
    instagram: { node: IG_NODE_METRICS, performance: IG_PERFORMANCE_METRICS, series: IG_SERIES },
    facebook: { node: FB_NODE_METRICS, performance: FB_PERFORMANCE_METRICS, series: FB_SERIES },
};

function MetricCard({ metrica, valor, sub, label }) {
    const tone = METRIC_TONES[metrica.tone] || METRIC_TONES.slate;
    return (
        <div className={d.metrics.card}>
            <div className={`${d.metrics.cardIcon} ${tone.bg}`}>
                <Iconify icon={metrica.icon} width={16} className={tone.text} />
            </div>
            <div className="min-w-0">
                <p className={d.metrics.cardValue}>{formatMetricValue(valor, metrica.format)}</p>
                <p className={d.metrics.cardLabel}>{label}</p>
                {sub ? <p className={d.metrics.cardSub}>{sub}</p> : null}
            </div>
        </div>
    );
}

/**
 * Uma secao mostra OU >= 1 tile OU exatamente uma linha de motivo. Nunca as
 * duas vazias — grade vazia sem explicacao e o estado que o painel existe para
 * eliminar.
 */
function GroupNote({ grupo, t, onReconnect }) {
    if (!grupo || grupo.status === 'ok') return null;

    const motivo = grupo.reason || 'UPSTREAM_ERROR';
    const texto = t(`socialAccounts.metrics.unavailable.${motivo}`, {
        defaultValue: t('socialAccounts.metrics.unavailable.UPSTREAM_ERROR'),
    });
    const podeAgir = !SEM_ACAO_DO_USUARIO.includes(motivo) && Boolean(onReconnect);
    const mudo = SEM_ACAO_DO_USUARIO.includes(motivo);

    return (
        <div className={mudo ? d.metrics.noteMuted : d.metrics.note}>
            <Iconify icon="eva:info-outline" width={13} className="flex-shrink-0 mt-px" />
            <span>
                {texto}
                {podeAgir ? (
                    <>
                        {' '}
                        <button type="button" className={d.metrics.noteAction} onClick={onReconnect}>
                            {t('socialAccounts.metrics.reconnect')}
                        </button>
                    </>
                ) : null}
            </span>
        </div>
    );
}

function Sparkline({ dados, cor }) {
    if (!dados?.length) return null;
    return (
        <div style={{ height: 90 }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dados} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                    <defs>
                        <linearGradient id={`grad-${cor.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={cor} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={cor} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <XAxis dataKey="date" hide />
                    <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                        labelStyle={{ fontSize: 10, color: '#64748b' }}
                    />
                    <Area
                        type="monotone" dataKey="value" stroke={cor} strokeWidth={2}
                        fill={`url(#grad-${cor.replace('#', '')})`} dot={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

/** Barras de proporcao — usadas em demografia e em seguidor vs nao-seguidor. */
function BarList({ itens, total }) {
    const max = total || Math.max(...itens.map((i) => i.value), 1);
    return (
        <div className="flex flex-col gap-1.5">
            {itens.map((item) => (
                <div key={item.label} className={d.metrics.barRow}>
                    <span className={d.metrics.barLabel} title={item.label}>{item.label}</span>
                    <span className={d.metrics.barTrack}>
                        <span
                            className={d.metrics.barFill}
                            style={{ width: `${Math.min(100, (item.value / max) * 100)}%`, display: 'block' }}
                        />
                    </span>
                    <span className={d.metrics.barValue}>{formatMetricValue(item.value)}</span>
                </div>
            ))}
        </div>
    );
}

export default function SocialMetricsPanel({ provider, accountId, node = {}, onReconnect }) {
    const { t, i18n } = useTranslation(['settings', 'common']);
    const catalogo = CATALOGO[provider];

    const [days, setDays] = useState(30);
    const [verPublico, setVerPublico] = useState(false);
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [escopoErro, setEscopoErro] = useState(null);
    const [erro, setErro] = useState(null);

    const carregar = useCallback(async (opts = {}) => {
        if (!accountId) return;
        setLoading(true);
        setErro(null);
        setEscopoErro(null);
        try {
            const fn = provider === 'instagram'
                ? mktAccountsService.getInstagramMetrics
                : mktAccountsService.getFacebookPageMetrics;
            const res = await fn(accountId, { days, includeAudience: verPublico, refresh: opts.refresh });
            setDados(res?.data || null);
        } catch (err) {
            const body = err?.response?.data;
            // 409: token morto ou escopo ausente para TUDO — o unico caso em que
            // nao ha metrica alguma para mostrar e o CTA de reconexao e a saida.
            if (err?.response?.status === 409) setEscopoErro(body);
            else setErro(body?.message || err?.message || t('socialAccounts.metrics.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [accountId, provider, days, verPublico, t]);

    useEffect(() => { carregar(); }, [carregar]);

    const totals = dados?.totals || {};
    // node do envelope tem prioridade (foi lido junto das metricas); o node vindo
    // do endpoint de perfil e o fallback quando a chamada de metricas falhou.
    const noFinal = useMemo(() => ({ ...node, ...(dados?.node || {}) }), [node, dados]);
    const groups = dados?.groups || {};

    const tilesNode = metricasDisponiveis(catalogo.node, noFinal);
    const tilesPerf = metricasDisponiveis(catalogo.performance, totals);

    const label = (m) => t(`socialAccounts.metrics.label.${m.i18n}`);
    const subDe = (m) => (
        m.i18n === 'rating' && noFinal.rating_count
            ? t('socialAccounts.metrics.ratingCount', { count: noFinal.rating_count })
            : null
    );

    // Grupos que alimentam a secao de desempenho — a nota mostrada e a do
    // primeiro que falhou, para nao empilhar 5 avisos identicos.
    const grupoPerf = useMemo(() => {
        const chaves = provider === 'instagram'
            ? ['totals']
            : ['totals', 'followers', 'engaged_lifetime'];
        return chaves.map((k) => groups[k]).find((g) => g && g.status !== 'ok') || groups.totals;
    }, [groups, provider]);

    const seriesDisponiveis = catalogo.series.filter((s) => dados?.series?.[s.metric]?.length);

    const porTipo = dados?.breakdowns?.media_product_type || {};
    const tiposOrdenados = Object.keys(porTipo).sort(
        (a, b) => MEDIA_PRODUCT_ORDER.indexOf(a) - MEDIA_PRODUCT_ORDER.indexOf(b)
    );

    const seguidorVsNao = dados?.breakdowns?.follow_type || {};
    const audiencia = dados?.audience || {};

    if (escopoErro) {
        return (
            <div className={d.metrics.wrapper}>
                <div className={d.metrics.note}>
                    <Iconify icon="eva:lock-outline" width={14} className="flex-shrink-0 mt-px" />
                    <span>
                        {t(`socialAccounts.metrics.unavailable.${escopoErro.kind}`, {
                            defaultValue: escopoErro.message,
                        })}
                        {escopoErro.missing_scopes?.length ? (
                            <span className="block mt-1 font-mono text-[10px] text-amber-700">
                                {escopoErro.missing_scopes.join(', ')}
                            </span>
                        ) : null}
                        {onReconnect ? (
                            <>
                                {' '}
                                <button type="button" className={d.metrics.noteAction} onClick={onReconnect}>
                                    {t('socialAccounts.metrics.reconnect')}
                                </button>
                            </>
                        ) : null}
                    </span>
                </div>
                {/* Os tiles de no vem do endpoint de perfil e nao dependem de
                    escopo de insights — continuam renderizando. */}
                {tilesNode.length ? (
                    <div className={d.metrics.gridWide}>
                        {tilesNode.map((m) => (
                            <MetricCard key={m.key} metrica={m} valor={noFinal[m.key]} label={label(m)} sub={subDe(m)} />
                        ))}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className={d.metrics.wrapper}>
            {/* 1. Sempre disponivel — sobrevive a qualquer falha de /insights */}
            {tilesNode.length ? (
                <div className={d.metrics.gridWide}>
                    {tilesNode.map((m) => (
                        <MetricCard key={m.key} metrica={m} valor={noFinal[m.key]} label={label(m)} sub={subDe(m)} />
                    ))}
                </div>
            ) : null}

            {dados?.stale ? (
                <div className={d.metrics.staleBanner}>
                    <Iconify icon="eva:clock-outline" width={13} />
                    {t('socialAccounts.metrics.stale', { time: formatHora(dados.fetched_at, i18n.language) })}
                </div>
            ) : null}

            {erro ? (
                <div className={d.metrics.note}>
                    <Iconify icon="eva:alert-circle-outline" width={13} className="flex-shrink-0 mt-px" />
                    <span>
                        {erro}{' '}
                        <button type="button" className={d.metrics.noteAction} onClick={() => carregar({ refresh: true })}>
                            {t('socialAccounts.metrics.retry')}
                        </button>
                    </span>
                </div>
            ) : null}

            {/* 2. Desempenho no periodo */}
            <div className={d.metrics.section}>
                <div className={d.metrics.sectionHead}>
                    <p className={d.metrics.sectionTitle}>
                        <Iconify icon="eva:bar-chart-outline" width={13} className="text-violet-600" />
                        {t('socialAccounts.metrics.section.performance', { days })}
                    </p>
                    <div className={d.metrics.periodRow}>
                        {PERIODOS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                className={p === days ? d.metrics.periodChipActive : d.metrics.periodChip}
                                onClick={() => setDays(p)}
                                disabled={loading}
                            >
                                {t('socialAccounts.metrics.period', { days: p })}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className={d.metrics.grid}>
                        {[0, 1, 2, 3, 4].map((i) => <div key={i} className={d.metrics.skeleton} />)}
                    </div>
                ) : tilesPerf.length ? (
                    <>
                        <div className={d.metrics.grid}>
                            {tilesPerf.map((m) => (
                                <MetricCard key={m.key} metrica={m} valor={totals[m.key]} label={label(m)} />
                            ))}
                        </div>
                        {grupoPerf?.metrics_dropped?.length ? (
                            <p className={d.metrics.cardSub}>
                                {t('socialAccounts.metrics.dropped', {
                                    metrics: grupoPerf.metrics_dropped.join(', '),
                                })}
                            </p>
                        ) : null}
                    </>
                ) : (
                    <GroupNote grupo={grupoPerf} t={t} onReconnect={onReconnect} />
                )}
            </div>

            {/* 3. Evolucao */}
            {!loading && seriesDisponiveis.length ? (
                <div className={d.metrics.section}>
                    <p className={d.metrics.sectionTitle}>
                        <Iconify icon="eva:trending-up-outline" width={13} className="text-violet-600" />
                        {t('socialAccounts.metrics.section.trend')}
                    </p>
                    <div className={d.metrics.chartGrid}>
                        {seriesDisponiveis.map((s) => (
                            <div key={s.metric} className={d.metrics.chartBox}>
                                <p className={d.metrics.chartTitle}>
                                    {t(`socialAccounts.metrics.label.${s.i18n}`)}
                                </p>
                                <Sparkline dados={dados.series[s.metric]} cor={s.color} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* 4. Por tipo de conteudo (so Instagram) */}
            {!loading && provider === 'instagram' && tiposOrdenados.length ? (
                <div className={d.metrics.section}>
                    <p className={d.metrics.sectionTitle}>
                        <Iconify icon="eva:layers-outline" width={13} className="text-violet-600" />
                        {t('socialAccounts.metrics.section.content')}
                    </p>
                    <div className="overflow-x-auto">
                        <table className={d.metrics.table}>
                            <thead>
                                <tr>
                                    <th className={d.metrics.th}>{t('socialAccounts.metrics.surface')}</th>
                                    <th className={d.metrics.th}>{t('socialAccounts.metrics.label.reach')}</th>
                                    <th className={d.metrics.th}>{t('socialAccounts.metrics.label.views')}</th>
                                    <th className={d.metrics.th}>{t('socialAccounts.metrics.label.totalInteractions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tiposOrdenados.map((tipo) => (
                                    <tr key={tipo}>
                                        <td className={`${d.metrics.td} font-medium`}>{tipo}</td>
                                        <td className={d.metrics.td}>{formatMetricValue(porTipo[tipo].reach || 0)}</td>
                                        <td className={d.metrics.td}>{formatMetricValue(porTipo[tipo].views || 0)}</td>
                                        <td className={d.metrics.td}>
                                            {formatMetricValue(porTipo[tipo].total_interactions || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {/* 5. Origem do alcance */}
            {!loading && Object.keys(seguidorVsNao).length ? (
                <div className={d.metrics.section}>
                    <p className={d.metrics.sectionTitle}>
                        <Iconify icon="eva:people-outline" width={13} className="text-violet-600" />
                        {t('socialAccounts.metrics.section.source')}
                    </p>
                    <div className={d.metrics.chartBox}>
                        <BarList
                            itens={Object.entries(seguidorVsNao).map(([k, v]) => ({
                                label: t(`socialAccounts.metrics.followType.${k}`, { defaultValue: k }),
                                value: v.reach || v.views || 0,
                            }))}
                        />
                    </div>
                </div>
            ) : null}

            {/* 6. Publico — colapsado, carrega sob demanda (4 chamadas extras) */}
            <div className={d.metrics.section}>
                <button
                    type="button"
                    className={d.metrics.collapseBtn}
                    onClick={() => setVerPublico((v) => !v)}
                >
                    <Iconify icon={verPublico ? 'eva:chevron-down-outline' : 'eva:chevron-right-outline'} width={14} />
                    {t('socialAccounts.metrics.section.audience')}
                </button>

                {verPublico && !loading ? (
                    <div className={d.metrics.chartGrid}>
                        {[
                            { chave: 'age', grupo: 'audience_age', i18n: 'age' },
                            { chave: 'gender', grupo: 'audience_gender', i18n: 'gender' },
                            { chave: 'city', grupo: 'audience_city', i18n: 'city' },
                            { chave: 'country', grupo: 'audience_geo', i18n: 'country' },
                        ].map(({ chave, grupo, i18n: chaveI18n }) => {
                            const itens = (audiencia[chave] || []).slice(0, 10);
                            if (!itens.length && !groups[grupo]) return null;
                            return (
                                <div key={chave} className={d.metrics.chartBox}>
                                    <p className={d.metrics.chartTitle}>
                                        {t(`socialAccounts.metrics.audience.${chaveI18n}`)}
                                    </p>
                                    {itens.length
                                        ? <BarList itens={itens} />
                                        : <GroupNote grupo={groups[grupo]} t={t} onReconnect={onReconnect} />}
                                </div>
                            );
                        })}

                        {audiencia.online_followers?.length ? (
                            <div className={d.metrics.chartBox}>
                                <p className={d.metrics.chartTitle}>
                                    {t('socialAccounts.metrics.audience.onlineFollowers')}
                                </p>
                                <Sparkline
                                    dados={audiencia.online_followers.map((h) => ({
                                        date: `${String(h.hour).padStart(2, '0')}h`,
                                        value: h.value,
                                    }))}
                                    cor="#7C3AED"
                                />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {/* 7. Rodape: a Meta atrasa os dados ate 48h — dizer isso evita
                 chamados de "o numero nao bate com o app". */}
            <div className={d.metrics.footer}>
                <span>{t('socialAccounts.metrics.delayHint')}</span>
                {dados?.fetched_at ? (
                    <span>
                        {t('socialAccounts.metrics.updatedAt', {
                            time: formatHora(dados.fetched_at, i18n.language),
                        })}
                    </span>
                ) : null}
                <button
                    type="button"
                    className={d.metrics.noteAction}
                    onClick={() => carregar({ refresh: true })}
                    disabled={loading}
                >
                    {t('socialAccounts.metrics.retry')}
                </button>
            </div>
        </div>
    );
}
